// src/components/CouplesList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { useAuth } from '../auth/AuthContext';
import auditLogger from '../utils/auditLogger';
import { isDancerCheckedIn, isDancerAllocatedToEvent, canDancerCheckIn } from '../utils/dancerValidation';
import { normalizeDancer, getDancersList } from '../utils/fieldCompatibility';
import ClothNumberPopup from './ClothNumberPopup';
import './CouplesList.css';
import CoupleForm from './CoupleForm';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const CouplesList = ({ readOnly = false }) => {
  const { selectedCompetition } = useCompetition();
  const { currentUser } = useAuth();
  const [couples, setCouples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCouple, setEditingCouple] = useState(null);
  const [updatingCheckin, setUpdatingCheckin] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [clubs, setClubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [dancerLocationCache, setDancerLocationCache] = useState(new Map());
  const [showClothPopup, setShowClothPopup] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState(new Set()); // Track which events are expanded
  const [selectedClothData, setSelectedClothData] = useState(null);

  // Get dancer location based on club - simple, synchronous lookup
  const getDancerLocation = (couple) => {
    // Check cache first (use both ID field names)
    const coupleId = couple.id || couple.couple_id;
    if (coupleId && dancerLocationCache.has(coupleId)) {
      return dancerLocationCache.get(coupleId);
    }

    // Fallback to couple country if not cached yet
    return couple.country || 'Unknown';
  };

  // Build dancer location cache from all couples data (called once)
  const buildDancerLocationCache = () => {
    if (clubs.length === 0) return;

  
    const newCache = new Map();

    couples.forEach(couple => {
      // Couples are already normalized, so use normalized fields
      let location = couple.country || 'Unknown';

      // Try to find club by clubId
      let club = null;
      if (couple.clubId) {
        club = clubs.find(c => c.id === couple.clubId);
      }

      // Try to find club by clubName
      if (!club && couple.clubName) {
        club = clubs.find(c => c.name.toLowerCase() === couple.clubName.toLowerCase());
      }

      // If club found with province and country, use that
      if (club && club.province && club.country) {
        location = `${club.province}, ${club.country}`;
      } else if (club && club.country) {
        location = club.country;
      }

      // Cache the result
      if (couple.id) {
        newCache.set(couple.id, location);
      }
    });

      setDancerLocationCache(newCache);
  };

  // Function to format dancer names based on dancer type
  const formatDancerNames = (couple) => {
    // Couples are already normalized, so use normalized fields
    const dancerType = couple.dancerType || 'couple'; // Default to couple for backward compatibility

    switch (dancerType) {
      case 'solo':
        return couple.leadName || 'Unknown Dancer';

      case 'team':
        if (couple.teamMembers && couple.teamMembers.length > 0) {
          return couple.teamMembers.join(' & ');
        }
        return couple.leadName || 'Unknown Team';

      case 'couple':
      default:
        // For couples or any other type, show leadName & followName
        const leadName = couple.leadName || '';
        const followName = couple.followName || '';

        if (leadName && followName) {
          return `${leadName} & ${followName}`;
        } else if (leadName) {
          return leadName;
        } else if (followName) {
          return followName;
        }

        // Fallback to name field if available
        if (couple.name && couple.name.trim() && !couple.name.includes('undefined')) {
          return couple.name.trim();
        }

        return 'Unknown Couple';
    }
  };

  // Function to get dancer type display label
  const getDancerTypeLabel = (couple) => {
    // Couples are already normalized, so use normalized fields
    const dancerType = couple.dancerType || 'couple';

    switch (dancerType) {
      case 'solo':
        return 'Solo';
      case 'team':
        return 'Team';
      case 'couple':
      default:
        return 'Couple / Duo';
    }
  };

  // Function to filter couples based on search query and check-in status
  const getFilteredCouples = () => {
    let filteredCouples = couples;

    // If in readOnly mode, only show checked-in dancers
    if (readOnly) {
      filteredCouples = filteredCouples.filter(isDancerCheckedIn);
    }

    // Apply search filter if there's a search query
    if (!searchQuery.trim()) {
      return filteredCouples;
    }

    const query = searchQuery.toLowerCase().trim();

    return filteredCouples.filter(couple => {
      // Couples are already normalized, so use normalized fields
      // Search by cloth number
      if (couple.clothNumber && couple.clothNumber.toLowerCase().includes(query)) {
        return true;
      }

      // Search by formatted dancer names
      const dancerNames = formatDancerNames(couple).toLowerCase();
      if (dancerNames.includes(query)) {
        return true;
      }

      // Search by individual name fields
      if (couple.leadName && couple.leadName.toLowerCase().includes(query)) {
        return true;
      }

      if (couple.followName && couple.followName.toLowerCase().includes(query)) {
        return true;
      }

      // Search by team members
      if (couple.teamMembers && couple.teamMembers.some(member =>
        member && member.toLowerCase().includes(query)
      )) {
        return true;
      }

      // Search by location (Province,Country or fallback country)
      const location = getDancerLocation(couple);
      if (location && location.toLowerCase().includes(query)) {
        return true;
      }

      // Search by club name
      const clubName = getClubNameById(couple.clubId);
      if (clubName && clubName.toLowerCase().includes(query)) {
        return true;
      }

      // Search by allocated event name
      const allocatedEventName = getAllocatedEventName(couple);
      if (allocatedEventName && allocatedEventName.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  };

  // Function to fetch clubs from API
  const fetchClubs = async () => {
    try {
      let url = `https://${_host}:6003/api/${_company}/clubs`;

      // Add competitionId filter if available
      if (selectedCompetition && selectedCompetition.id) {
        url += `?competitionId=${selectedCompetition.id}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const clubsData = await response.json();
        setClubs(clubsData);
      }
    } catch (err) {
      console.error('Error fetching clubs:', err);
    }
  };

  // Fetch events from API
  const fetchEvents = async () => {
    try {
      let url = `https://${_host}:6003/api/${_company}/events`;
      if (selectedCompetition && selectedCompetition.id) {
        url += `?competitionId=${selectedCompetition.id}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const eventsData = await response.json();
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setEvents([]);
    }
  };

  // Function to get club name by ID
  const getClubNameById = useCallback((clubId) => {
    if (!clubId) return 'No Club';
    const club = clubs.find(c => c.id === clubId);
    return club ? club.name : 'Unknown Club';
  }, [clubs]);

  // Function to get allocated event name for a dancer
  const getAllocatedEventName = useCallback((couple) => {
    if (!events || events.length === 0) return 'No event';

    // Couples are already normalized, so use normalized fields
    // Find events where this dancer is allocated
    const clothNumber = couple.clothNumber;
    const allocatedEvents = events.filter(event => {
      if (!event) return false;

      // Use getDancersList to properly handle both JSON strings and arrays
      const dancers = getDancersList(event);
      if (!Array.isArray(dancers)) return false;

      return dancers.some(dancer => {
        const dancerClothNumber = dancer.clothNumber || dancer.cloth_number;
        return dancerClothNumber === clothNumber;
      });
    });

    if (allocatedEvents.length === 0) {
      return 'No event';
    }

    // Return the first allocated event name
    return allocatedEvents[0].name;
  }, [events]);

  // NEW FUNCTION: Get ALL events for a couple (for cloth number printing)
  const getAllCoupleEvents = useCallback((couple) => {
    if (!events || events.length === 0) return 'No events';

    // Couples are already normalized, so use normalized fields
    const clothNumber = couple.clothNumber;
    const coupleId = couple.id;

    console.log(`🎯 Getting ALL events for couple: ID=${coupleId}, Cloth=${clothNumber}`);

    // Find all events where this dancer is allocated
    const allocatedEvents = events.filter(event => {
      if (!event) return false;

      // Use getDancersList to properly handle both JSON strings and arrays
      const dancers = getDancersList(event);
      if (!Array.isArray(dancers)) return false;

      return dancers.some(dancer => {
        const dancerClothNumber = dancer.clothNumber || dancer.cloth_number;
        const dancerId = dancer.id || dancer.dancerId;

        // Match by both cloth number and ID for accuracy
        return (dancerClothNumber === clothNumber) || (dancerId === coupleId);
      });
    });

    if (allocatedEvents.length === 0) {
      console.log(`❌ No events found for couple ${clothNumber}`);
      return 'No events';
    }

    // Return ALL allocated event names as comma-separated list
    const allEventNames = allocatedEvents.map(event => event.name).join(', ');
    console.log(`✅ Found ${allocatedEvents.length} events for couple ${clothNumber}: ${allEventNames}`);

    return allEventNames;
  }, [events]);

  // Toggle event expansion for a couple
  const toggleEventExpansion = (coupleId) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(coupleId)) {
        newSet.delete(coupleId);
      } else {
        newSet.add(coupleId);
      }
      return newSet;
    });
  };

  // Handle cloth number click to show popup
  const handleClothNumberClick = (couple) => {
    // Couples are already normalized, so use normalized fields
    const dancerNames = formatDancerNames(couple);
    const clubName = getClubNameById(couple.clubId);
    const clothNumber = couple.clothNumber;
    const allEventNames = getAllCoupleEvents(couple); // Use NEW function that returns ALL events

    setSelectedClothData({
      clothNumber: clothNumber,
      clubStudio: clubName,
      coupleNames: dancerNames,
      eventTitle: allEventNames // Now contains ALL events, not just the first one
    });
    setShowClothPopup(true);
  };

  
  // Fetch payment status data and merge with couples
  const fetchPaymentStatuses = async () => {
    try {
      let url = `https://${_host}:6003/api/${_company}/payments`;

      // Add competitionId filter if available
      if (selectedCompetition && selectedCompetition.id) {
        url += `?competitionId=${selectedCompetition.id}`;
      }

      const response = await fetch(url);

      if (response.ok) {
        const paymentsData = await response.json();

        // Create a map of dancerId to paymentStatus for quick lookup
        const paymentStatusMap = new Map();
        paymentsData.forEach(dancer => {
          paymentStatusMap.set(dancer.id, dancer.paymentStatus);
        });

        return paymentStatusMap;
      }
    } catch (err) {
      console.error('Error fetching payment statuses:', err);
      return new Map();
    }
  };

  // Fetch only payment status updates (for real-time updates)
  const fetchPaymentStatusUpdates = async () => {
    try {
      const paymentStatusMap = await fetchPaymentStatuses();

      // Update couples with new payment status without full refresh
      setCouples(prevCouples =>
        prevCouples.map(couple => {
          const newPaymentStatus = paymentStatusMap.has(couple.id)
            ? paymentStatusMap.get(couple.id)
            : couple.paymentStatus; // Keep existing status if not found

          // Only update if payment status changed
          if (couple.paymentStatus !== newPaymentStatus) {
            console.log(`💳 Payment status updated for dancer ${couple.id}: ${couple.paymentStatus} → ${newPaymentStatus}`);
            return {
              ...couple,
              paymentStatus: newPaymentStatus
            };
          }

          return couple;
        })
      );
    } catch (err) {
      console.error('Error fetching payment status updates:', err);
      // Don't show error to user for background updates
    }
  };

  // Fetch couples from the API
  const fetchCouples = async () => {
    try {
      setLoading(true);

      let data;

      // Fetch both couples and payment status data in parallel
      const [couplesResponse, paymentStatusMap] = await Promise.all([
        selectedCompetition
          ? fetch(`https://${_host}:6003/api/${_company}/couples?competitionId=${selectedCompetition.id}`)
          : fetch(`https://${_host}:6003/api/${_company}/couples`),
        fetchPaymentStatuses()
      ]);

      if (couplesResponse.ok) {
        data = await couplesResponse.json();
        console.log('Fetched couples data:', data?.length || 0, 'couples');
        console.log('Fetched payment statuses:', paymentStatusMap.size, 'entries');

        // Normalize couples data and merge payment status
        const normalizedCouples = (data || []).map(couple => {
          const normalizedCouple = normalizeDancer(couple);
          // Merge payment status from the payments API
          const paymentStatus = paymentStatusMap.has(normalizedCouple.id)
            ? paymentStatusMap.get(normalizedCouple.id)
            : false; // Default to unpaid if not found
          return {
            ...normalizedCouple,
            paymentStatus
          };
        });

        setCouples(normalizedCouples);
        setLoading(false);
        return;
      } else {
        throw new Error('Failed to fetch couples');
      }
    } catch (err) {
      console.error('Error fetching couples:', err);
      setError('Failed to load dancers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Load couples, clubs, and events when component mounts or when competition changes
  useEffect(() => {
    fetchCouples();
    fetchClubs();
    fetchEvents();
  }, [selectedCompetition]);

  // Set up periodic payment status updates for real-time awareness
  useEffect(() => {
    if (readOnly) {
      // Don't set up polling for read-only views (like callbacks)
      return;
    }

    console.log('🔄 Starting real-time payment status updates...');

    // Fetch payment status updates every 5 seconds
    const paymentUpdateInterval = setInterval(() => {
      fetchPaymentStatusUpdates();
    }, 5000); // 5-second interval for payment status changes

    // Cleanup interval on component unmount or competition change
    return () => {
      console.log('⏹️ Stopping real-time payment status updates');
      clearInterval(paymentUpdateInterval);
    };
  }, [selectedCompetition, readOnly]); // Re-setup when competition changes or read-only mode changes

  // Update couples with club names when clubs are loaded
  useEffect(() => {
    if (clubs.length > 0) {
      setCouples(prevCouples =>
        prevCouples.map(couple => ({
          ...couple,
          clubName: getClubNameById(couple.clubId)
        }))
      );
    }
  }, [clubs, getClubNameById]);

  // Build dancer location cache when both couples and clubs are loaded
  useEffect(() => {
    if (couples.length > 0 && clubs.length > 0) {
      const timer = setTimeout(() => {
        buildDancerLocationCache();
      }, 1000); // Wait 1 second after data is loaded

      return () => clearTimeout(timer);
    }
  }, [couples, clubs]);

  // Force hide form when in readOnly mode
  useEffect(() => {
    if (readOnly) {
      setShowForm(false);
      setEditingCouple(null);
    }
  }, [readOnly]);

  // Listen for club changes that affect couples
  useEffect(() => {
    const handleCouplesDataChanged = () => {
      fetchCouples();
    };

    // Add event listener
    window.addEventListener('couplesDataChanged', handleCouplesDataChanged);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('couplesDataChanged', handleCouplesDataChanged);
    };
  }, []); // Empty dependency array means this runs only once on mount

  // Handle delete couple
  const handleDeleteCouple = async (coupleId, dancerNames) => {
    try {
      // Show confirmation dialog with dancer names and warning about allocations
      const confirmDelete = window.confirm(
        `Are you sure you want to delete "${dancerNames}"? \n\n` +
        `This will:\n` +
        `• Delete the dancer(s) from the system\n` +
        `• Remove them from all event allocations\n` +
        `• This action cannot be undone`
      );

      if (!confirmDelete) {
        return;
      }

      // First, get all events to find where this dancer is allocated
      const eventsResponse = await fetch(`https://${_host}:6003/api/${_company}/events`);
      if (!eventsResponse.ok) {
        throw new Error('Failed to fetch events for cleanup');
      }
      const allEvents = await eventsResponse.json();

      // Find events that contain this dancer
      const eventsToUpdate = [];
      const coupleClothNumber = couples.find(c => c.id === coupleId)?.clothNumber;

      if (coupleClothNumber) {
        for (const event of allEvents) {
          const eventDancers = getDancersList(event);
          const hasDancer = eventDancers.some(dancer =>
            (dancer.cloth_number === coupleClothNumber || dancer.clothNumber === coupleClothNumber) ||
            dancer.coupleId === coupleId
          );

          if (hasDancer) {
            eventsToUpdate.push(event);
          }
        }
      }

      // Update events to remove the dancer
      let cleanupCount = 0;
      for (const event of eventsToUpdate) {
        try {
          const currentDancers = getDancersList(event);
          const updatedDancers = currentDancers.filter(dancer =>
            (dancer.cloth_number !== coupleClothNumber && dancer.clothNumber !== coupleClothNumber) &&
            dancer.coupleId !== coupleId
          );

          // Update the event with filtered dancers
          const updateResponse = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dancers: updatedDancers })
          });

          if (updateResponse.ok) {
            cleanupCount++;
            console.log(`✅ Removed dancer from event: ${event.name}`);
          } else {
            console.warn(`⚠️ Failed to update event: ${event.name}`);
          }
        } catch (eventError) {
          console.error(`Error updating event ${event.name}:`, eventError);
        }
      }

      // Now delete the couple from the couples table
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples/${coupleId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete dancer(s)');
      }

      // Log dancer deletion with cleanup information
      if (currentUser) {
        auditLogger.logDeleteDancer(
          currentUser.username || currentUser.id,
          `${dancerNames} (cleaned from ${cleanupCount} events)`,
          coupleId
        );
      }

      let successMessage = 'Dancer(s) deleted successfully';
      if (cleanupCount > 0) {
        successMessage += ` and removed from ${cleanupCount} event${cleanupCount !== 1 ? 's' : ''}`;
      }

      setSuccess(successMessage);
      setTimeout(() => setSuccess(null), 5000);

      // Refresh both couples and events data
      fetchCouples();
      // Trigger event data refresh for other components
      window.dispatchEvent(new CustomEvent('dancersDataChanged', {
        detail: { action: 'delete', coupleId, cleanupCount }
      }));

    } catch (err) {
      console.error('Error deleting dancer(s):', err);
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle Edit Dancer(s)
  const handleEditCouple = (couple) => {
    setEditingCouple(couple);
    setShowForm(true);
  };

  
  // Handle form submission (add/edit)
  const handleCoupleSubmitted = async (couple) => {
    // Log dancer creation or update
    if (currentUser && couple) {
      const dancerName = formatDancerNames(couple);
      if (editingCouple) {
        // Update operation
        auditLogger.logUpdateDancer(
          currentUser.username || currentUser.id,
          dancerName,
          couple.id
        );
      } else {
        // Create operation
        auditLogger.logCreateDancer(
          currentUser.username || currentUser.id,
          dancerName,
          couple.id
        );
      }
    }

    // Refresh couples list
    fetchCouples();
    setShowForm(false);
    setEditingCouple(null);
    setSuccess('Dancer(s) saved successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Handle check-in status updates
  const handleCheckinUpdate = async (coupleId, currentValue) => {
    try {
      setUpdatingCheckin(prev => ({ ...prev, [`${coupleId}-registrationCheckin`]: true }));

      const couple = couples.find(c => c.id === coupleId);
      const newValue = !currentValue;

      // Check payment status - prevent check-in if not paid
      if (newValue && !couple.paymentStatus) {
        setError('Cannot check in dancer: Payment status is "Not Paid". Please mark as paid before check-in.');
        setTimeout(() => setError(null), 5000);
        setUpdatingCheckin(prev => ({ ...prev, [`${coupleId}-registrationCheckin`]: false }));
        return;
      }

      // For Registration check-in, verify dancer is allocated first
      if (newValue) {
        const { canCheckIn, message } = canDancerCheckIn(couple, events);
        if (!canCheckIn) {
          setError(message);
          setTimeout(() => setError(null), 5000);
          setUpdatingCheckin(prev => ({ ...prev, [`${coupleId}-registrationCheckin`]: false }));
          return;
        }
      }

      // Update local state immediately for better UX (optimistic update)
      setCouples(prevCouples =>
        prevCouples.map(c => {
          if (c.id === coupleId) {
            return { ...c, registrationCheckin: newValue };
          }
          return c;
        })
      );

      // Create API data object with only registration check-in field
      const apiData = {
        id: coupleId,
        competitionId: couple?.competitionId,
        registrationCheckin: newValue
      };

      // Update the backend
      let response;
      try {
        response = await fetch(`https://${_host}:6003/api/${_company}/couples/${coupleId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiData)
        });
      } catch (fetchError) {
        throw fetchError;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update check-in status');
      }

      // Log the check-in action
      if (currentUser) {
        const dancerName = couple ? formatDancerNames(couple) : 'Unknown Dancer';
        const action = newValue ? 'checked in' : 'checked out';

        auditLogger.log(
          currentUser.username || currentUser.id,
          `Registration Check-in ${action}`,
          `${dancerName} ${action.toLowerCase()} for Registration`,
          coupleId,
          'Couple'
        );
      }

      setSuccess('Registration check-in updated successfully');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error updating check-in status:', err);

      // Revert local state on error
      setCouples(prevCouples =>
        prevCouples.map(c => {
          if (c.id === coupleId) {
            return { ...c, registrationCheckin: currentValue };
          }
          return c;
        })
      );

      setError('Failed to update check-in status. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setUpdatingCheckin(prev => ({ ...prev, [`${coupleId}-registrationCheckin`]: false }));
    }
  };

  // Function to generate Dancers List PDF with checkboxes for manual tracking
  const generateDancersListPDF = () => {
    try {
      console.log('📄 Generating Dancers List PDF...');

      // Create new PDF document - Landscape for better space
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Get competition name
      const competitionName = selectedCompetition?.name || 'Competition';

      // Set font and add header
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(competitionName, doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Dancers Registration Checklist', doc.internal.pageSize.getWidth() / 2, 30, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.getWidth() / 2, 38, { align: 'center' });

      // Filter couples for the selected competition - more inclusive filtering
      const filteredCouples = couples.filter(couple => {
        // Get both possible field names
        const coupleCompId = couple.competitionId || couple.competition_id || couple.competition;
        const selectedCompId = selectedCompetition?.id || selectedCompetition?.competition_id;

        // More inclusive filtering - include if competition IDs match OR if either is missing/undefined
        return (coupleCompId === selectedCompId) ||
               (!coupleCompId && !selectedCompId) ||
               (coupleCompId && !selectedCompId) ||
               (!coupleCompId && selectedCompId);
      });

      // Debug logging - comprehensive structure analysis
      console.log('📊 Total couples loaded:', couples.length);
      console.log('📊 Filtered couples for competition:', filteredCouples.length);
      if (filteredCouples.length > 0) {
        // Log complete structure of first few couples to find club field
        console.log('🔍 COMPLETE STRUCTURE ANALYSIS:');
        for (let i = 0; i < Math.min(10, filteredCouples.length); i++) {
          const couple = filteredCouples[i];
          console.log(`\n🔍 Couple ${i + 1} (Cloth #${couple?.cloth_number || couple?.clothNumber || couple?.number}):`);

          // Log ALL possible keys in this object
          const allKeys = Object.keys(couple);
          console.log('All keys:', allKeys);
          console.log('All keys (expanded):', JSON.stringify(allKeys, null, 2));

          // Log the entire object structure - force stringification
          console.log('Complete couple object:', JSON.stringify(couple, null, 2));

          // Check dancers array specifically
          if (couple.dancers) {
            console.log('Dancers array:', couple.dancers);
            if (Array.isArray(couple.dancers) && couple.dancers.length > 0) {
              console.log('First dancer structure:', couple.dancers[0]);
              console.log('First dancer keys:', Object.keys(couple.dancers[0]));
            }
          }

          // Check dancers_list
          if (couple.dancers_list) {
            console.log('Dancers list:', couple.dancers_list);
          }
        }

        // Find couples that might have club-like information
        console.log('\n🔍 SEARCHING FOR CLUB-LIKE FIELDS:');
        const sampleCouple = filteredCouples[0];
        const allSampleKeys = Object.keys(sampleCouple);

        console.log('All sample fields with values:');
        allSampleKeys.forEach(key => {
          const value = sampleCouple[key];
          console.log(`  ${key}: "${value}" (${typeof value})`);

          if (typeof value === 'string') {
            if (value.toLowerCase().includes('club')) {
              console.log(`✅ Found club-like field: ${key} = "${value}"`);
            }
            if (value.toLowerCase().includes('studio')) {
              console.log(`✅ Found studio-like field: ${key} = "${value}"`);
            }
            if (value.toLowerCase().includes('academy')) {
              console.log(`✅ Found academy-like field: ${key} = "${value}"`);
            }
            if (value.toLowerCase().includes('school')) {
              console.log(`✅ Found school-like field: ${key} = "${value}"`);
            }
            if (value.toLowerCase().includes('team')) {
              console.log(`✅ Found team-like field: ${key} = "${value}"`);
            }
            if (value.toLowerCase().includes('organization')) {
              console.log(`✅ Found organization-like field: ${key} = "${value}"`);
            }
          }
        });

        // Summary: Count couples with any potential club/studio/team data
        console.log('\n📊 CLUB DATA SUMMARY:');
        let couplesWithClubData = 0;
        let couplesWithStudioData = 0;
        let couplesWithTeamData = 0;
        let couplesWithOrgData = 0;

        filteredCouples.forEach((couple, index) => {
          Object.keys(couple).forEach(key => {
            const value = couple[key];
            if (typeof value === 'string') {
              if (value.toLowerCase().includes('club')) couplesWithClubData++;
              if (value.toLowerCase().includes('studio')) couplesWithStudioData++;
              if (value.toLowerCase().includes('team')) couplesWithTeamData++;
              if (value.toLowerCase().includes('organization')) couplesWithOrgData++;
            }
          });
        });

        console.log(`Total couples with 'club' in any field: ${couplesWithClubData}`);
        console.log(`Total couples with 'studio' in any field: ${couplesWithStudioData}`);
        console.log(`Total couples with 'team' in any field: ${couplesWithTeamData}`);
        console.log(`Total couples with 'organization' in any field: ${couplesWithOrgData}`);
        console.log(`Total couples analyzed: ${filteredCouples.length}`);
      }

      // Function to get all events for a couple
      const getCoupleEvents = (couple) => {
        if (!events || !Array.isArray(events)) return '';

        // Get couple ID using multiple possible field names
        const coupleId = couple.id || couple.couple_id;
        const clothNumber = couple.cloth_number || couple.clothNumber || couple.number;
        const coupleCloth = clothNumber || coupleId;

        // Debug logging for specific couple 798
        if (clothNumber === '798' || coupleId === '798') {
          console.log(`🔍 DEBUGGING COUPLE 798: Cloth=${clothNumber}, ID=${coupleId}`);
          console.log(`🔍 Total events available: ${events.length}`);
        }

        // Find all events where this couple is registered
        const coupleEvents = [];
        events.forEach((event, eventIndex) => {
          if (!event.dancers && !event.dancers_list) return;

          // Parse dancers from event (handle both string and array formats)
          let eventDancers = [];
          if (event.dancers) {
            if (typeof event.dancers === 'string') {
              try {
                eventDancers = JSON.parse(event.dancers);
              } catch (e) {
                eventDancers = [];
              }
            } else if (Array.isArray(event.dancers)) {
              eventDancers = event.dancers;
            }
          } else if (event.dancers_list) {
            if (typeof event.dancers_list === 'string') {
              try {
                eventDancers = JSON.parse(event.dancers_list);
              } catch (e) {
                eventDancers = [];
              }
            } else if (Array.isArray(event.dancers_list)) {
              eventDancers = event.dancers_list;
            }
          }

          // Check if couple is in this event by matching cloth number or couple ID
          const isCoupleInEvent = eventDancers.some(dancer => {
            const dancerClothNumber = dancer?.cloth_number || dancer?.clothNumber || dancer?.number;
            const dancerId = dancer?.id || dancer?.couple_id;
            const match = (dancerClothNumber && dancerClothNumber === clothNumber) ||
                         (dancerId && dancerId === coupleId);

            if (clothNumber === '798' || coupleId === '798') {
              console.log(`🔍 Event ${eventIndex}: ${event.name || event.event_name}`);
              console.log(`   - Checking dancer: Cloth=${dancerClothNumber}, ID=${dancerId}`);
              console.log(`   - Match: ${match}`);
            }

            return match;
          });

          if (isCoupleInEvent) {
            const eventName = event.name || event.event_name || 'Unknown Event';
            coupleEvents.push(eventName);

            if (clothNumber === '798' || coupleId === '798') {
              console.log(`✅ Found event: ${eventName}`);
            }
          }
        });

        // Debug for couple 798
        if (clothNumber === '798' || coupleId === '798') {
          console.log(`🔍 Final events for couple 798: [${coupleEvents.join(', ')}]`);
        }

        // Return event names as comma-separated string
        return coupleEvents.length > 0 ? coupleEvents.join(', ') : '';
      };

      if (filteredCouples.length === 0) {
        doc.setFontSize(12);
        doc.text('No dancers found for this competition.', 20, 60);
      } else {
        // Prepare table data
        const tableData = filteredCouples.map((couple, index) => {
          // Get dancers list and format it properly
          const dancersArray = getDancersList(couple);
          let dancersList = 'N/A';

          if (Array.isArray(dancersArray) && dancersArray.length > 0) {
            // Format each dancer's name from the array
            const formattedDancers = dancersArray.map(dancer => {
              const leadName = dancer?.lead_name || dancer?.leadName || dancer?.name || '';
              const followName = dancer?.follow_name || dancer?.followName || '';
              return leadName && followName ? `${leadName} / ${followName}` : (leadName || followName || 'Unknown');
            }).filter(name => name && name !== 'Unknown');

            dancersList = formattedDancers.length > 0 ? formattedDancers.join(', ') : 'N/A';
          } else if (typeof dancersArray === 'string' && dancersArray.trim()) {
            dancersList = dancersArray;
          } else {
            // Fallback to individual dancer fields if no dancers array
            const leadName = couple?.lead_name || couple?.leadName || '';
            const followName = couple?.follow_name || couple?.followName || '';
            dancersList = leadName && followName ? `${leadName} / ${followName}` : (leadName || followName || 'N/A');
          }

          // Get cloth number with proper field mapping
          const clothNumber = couple.cloth_number || couple.clothNumber || couple.number || '';

          // Get club with enhanced field mapping
          const club = couple.club ||
                      couple.club_name ||
                      couple.clubName ||
                      couple.organization ||
                      couple.studio ||
                      couple.team ||
                      couple.school ||
                      couple.academy ||
                      // Check nested objects that might contain club info
                      (typeof couple.lead === 'object' ? couple.lead?.club : '') ||
                      (typeof couple.lead_name === 'object' ? couple.lead_name?.club : '') ||
                      (typeof couple.leadName === 'object' ? couple.leadName?.club : '') ||
                      // Check if club is stored in dancers array
                      (Array.isArray(dancersArray) && dancersArray[0]?.club) ||
                      (Array.isArray(dancersArray) && dancersArray[0]?.organization) ||
                      (Array.isArray(dancersArray) && dancersArray[0]?.studio) ||
                      (Array.isArray(dancersArray) && dancersArray[0]?.team) ||
                      '';

          const country = couple.country || couple.nationality || '';
          const status = couple.status || 'Active';

          // Get all events for this couple
          const coupleEvents = getCoupleEvents(couple);

          // Format cloth number with events
          const clothNumberWithEvents = clothNumber ?
            (coupleEvents ? `${clothNumber} (${coupleEvents})` : clothNumber) : 'N/A';

          return [
            index + 1,
            clothNumberWithEvents,
            dancersList,
            club || 'N/A',
            country || 'N/A',
            status || 'Active'
          ];
        });

        // Add table using autoTable
        autoTable(doc, {
          head: [
            [
              '#',
              'Cloth # (Events)',
              'Dancers',
              'Club',
              'Country',
              'Status'
            ]
          ],
          body: tableData,
          startY: 50,
          theme: 'grid',
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontSize: 10,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 9
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          columnStyles: {
            0: { cellWidth: 20 }, // #
            1: { cellWidth: 60 }, // Cloth # with Events (wider to accommodate event names)
            2: { cellWidth: 90 }, // Dancers (slightly reduced to make room)
            3: { cellWidth: 40 }, // Club (wider)
            4: { cellWidth: 30 }, // Country
            5: { cellWidth: 25 }  // Status
          },
          margin: { left: 10, right: 10 }
        });

        // Add manual tracking section - completely rewritten with proper page management
        const pageHeight = doc.internal.pageSize.getHeight(); // 210mm for A4
        const checkboxX = 25;
        const contentStartY = 30; // Start higher on page to leave more space
        const bottomMargin = 20; // Smaller bottom margin
        const checkboxAvailableSpace = pageHeight - contentStartY - bottomMargin; // ~160mm available
        const lineHeight = 6; // Tighter spacing for checkboxes
        const dancersPerPage = Math.floor(checkboxAvailableSpace / lineHeight) - 2; // Leave 2 rows as buffer

        console.log(`📏 Checkbox layout: contentStartY=${contentStartY}mm, checkboxAvailableSpace=${checkboxAvailableSpace}mm, lineHeight=${lineHeight}mm, dancersPerPage=${dancersPerPage}`);

        // Function to create checkbox headers at specific position
        const createCheckboxHeaders = (yPosition, pageNumber = null) => {
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          const title = pageNumber ? `Manual Tracking Checkboxes (Page ${pageNumber}):` : 'Manual Tracking Checkboxes:';
          doc.text(title, 20, yPosition);

          const headerY = yPosition + 10;
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text('#', checkboxX - 5, headerY);
          doc.text('Paid', checkboxX + 15, headerY);
          doc.text('Reg Check-in', checkboxX + 45, headerY);
          doc.text('Marshall Check-in', checkboxX + 85, headerY);
          doc.text('Cloth #', checkboxX + 135, headerY);
          doc.text('Dancers', checkboxX + 165, headerY);
          doc.setFont(undefined, 'normal');

          return headerY + 12; // Return position where content should start (12mm below headers)
        };

        // Function to extract dancer information
        const getDancerInfo = (couple) => {
          const dancersArray = getDancersList(couple);
          let dancersList = 'N/A';

          if (Array.isArray(dancersArray) && dancersArray.length > 0) {
            const formattedDancers = dancersArray.map(dancer => {
              const leadName = dancer?.lead_name || dancer?.leadName || dancer?.name || '';
              const followName = dancer?.follow_name || dancer?.followName || '';
              return leadName && followName ? `${leadName} / ${followName}` : (leadName || followName || 'Unknown');
            }).filter(name => name && name !== 'Unknown');

            dancersList = formattedDancers.length > 0 ? formattedDancers.join(', ') : 'N/A';
          } else if (typeof dancersArray === 'string' && dancersArray.trim()) {
            dancersList = dancersArray;
          } else {
            const leadName = couple?.lead_name || couple?.leadName || '';
            const followName = couple?.follow_name || couple?.followName || '';
            dancersList = leadName && followName ? `${leadName} / ${followName}` : (leadName || followName || 'N/A');
          }

          const clothNumber = couple.cloth_number || couple.clothNumber || couple.number || '';

          // For checkbox section, return just the cloth number (no events to keep it clean)
          const clothNumberForCheckbox = clothNumber || 'N/A';

          // Enhanced club extraction - same as main table
          const dancersArrayForClub = getDancersList(couple);
          const club = couple.club ||
                      couple.club_name ||
                      couple.clubName ||
                      couple.organization ||
                      couple.studio ||
                      couple.team ||
                      couple.school ||
                      couple.academy ||
                      // Check nested objects that might contain club info
                      (typeof couple.lead === 'object' ? couple.lead?.club : '') ||
                      (typeof couple.lead_name === 'object' ? couple.lead_name?.club : '') ||
                      (typeof couple.leadName === 'object' ? couple.leadName?.club : '') ||
                      // Check if club is stored in dancers array
                      (Array.isArray(dancersArrayForClub) && dancersArrayForClub[0]?.club) ||
                      (Array.isArray(dancersArrayForClub) && dancersArrayForClub[0]?.organization) ||
                      (Array.isArray(dancersArrayForClub) && dancersArrayForClub[0]?.studio) ||
                      (Array.isArray(dancersArrayForClub) && dancersArrayForClub[0]?.team) ||
                      '';

          return { dancersList, clothNumber: clothNumberForCheckbox, club };
        };

        // Calculate if we need a new page for checkboxes - be more conservative
        const tableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 60;
        const tableAvailableSpace = pageHeight - tableEndY - 40; // Reserve space for footer

        // Debug the space calculation
        console.log(`📏 Space calculation: tableEndY=${tableEndY}, tableAvailableSpace=${tableAvailableSpace}, pageHeight=${pageHeight}`);

        // Always use a new page for checkboxes to ensure we have enough space
        console.log('📄 Adding new page for checkboxes section');
        doc.addPage();

        // Process all dancers across multiple pages
        const totalPagesNeeded = Math.ceil(filteredCouples.length / dancersPerPage);
        let globalIndex = 0;

        console.log('📋 PDF Generation - Checkboxes Section:');
        console.log(`- Total filtered couples: ${filteredCouples.length}`);
        console.log(`- Dancers per page: ${dancersPerPage}`);
        console.log(`- Total pages needed: ${totalPagesNeeded}`);

        for (let pageNum = 1; pageNum <= totalPagesNeeded; pageNum++) {
          // Add new page for subsequent pages
          if (pageNum > 1) {
            doc.addPage();
          }

          // Create headers for current page and get the actual content start position
          const actualContentStartY = createCheckboxHeaders(contentStartY, pageNum);

          // Calculate dancers for this page
          const startIndex = (pageNum - 1) * dancersPerPage;
          const endIndex = Math.min(startIndex + dancersPerPage, filteredCouples.length);
          const couplesForThisPage = filteredCouples.slice(startIndex, endIndex);

          console.log(`- Page ${pageNum}: dancers ${startIndex + 1} to ${endIndex} (${couplesForThisPage.length} dancers), contentStartY=${actualContentStartY.toFixed(1)}mm`);

          // Add checkbox rows for this page
          couplesForThisPage.forEach((couple, pageIndex) => {
            const { dancersList, clothNumber } = getDancerInfo(couple);
            const currentY = actualContentStartY + (pageIndex * lineHeight);

            // Debug every dancer to see exactly what's happening
            if (pageIndex < 3 || pageIndex >= dancersPerPage - 3) {
              console.log(`🔍 Processing dancer ${startIndex + pageIndex + 1}: Cloth=${clothNumber}, Y=${currentY.toFixed(1)}, actualContentStartY=${actualContentStartY.toFixed(1)}, pageIndex=${pageIndex}`);
            }

            // Ensure we don't go off page - use the calculated bottom margin
            if (currentY > pageHeight - bottomMargin) {
              console.log(`⚠️ Would go off page at Y=${currentY.toFixed(1)}, skipping dancer ${startIndex + pageIndex + 1}`);
              return; // Skip if would go off page
            }

            // Draw checkboxes (simple squares) - aligned with headers
            const checkboxSize = 5;
            doc.rect(checkboxX + 10, currentY - 3, checkboxSize, checkboxSize); // Paid checkbox (aligned with "Paid" header)
            doc.rect(checkboxX + 40, currentY - 3, checkboxSize, checkboxSize); // Reg Check-in checkbox (aligned with "Reg Check-in" header)
            doc.rect(checkboxX + 80, currentY - 3, checkboxSize, checkboxSize); // Marshall Check-in checkbox (aligned with "Marshall Check-in" header)

            // Add text - properly aligned with headers
            const currentIndex = startIndex + pageIndex;
            doc.text((currentIndex + 1).toString(), checkboxX - 5, currentY); // Aligned with "#" header
            doc.text(clothNumber || 'N/A', checkboxX + 135, currentY); // Aligned with "Cloth #" header

            // Better dancer name handling for landscape
            const maxLength = 60; // Increased for landscape
            const truncatedDancers = dancersList.length > maxLength
              ? dancersList.substring(0, maxLength) + '...'
              : dancersList;
            doc.text(truncatedDancers, checkboxX + 165, currentY);
          });

          // Add summary note for each page
          doc.setFontSize(8);
          doc.setTextColor(128, 128, 128);
          const actualProcessed = startIndex + couplesForThisPage.length;
          const summaryText = `Page ${pageNum}: Processed ${startIndex + 1}-${actualProcessed} (${couplesForThisPage.length} dancers) of ${filteredCouples.length} total`;
          doc.text(summaryText, 20, pageHeight - 10);
          doc.setTextColor(0, 0, 0);

          console.log(`✅ Page ${pageNum} completed: Processed ${couplesForThisPage.length} dancers`);
        }
      }

      // Add footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 10, { align: 'center' });

        // Add Flokraft attribution in smallest font
        doc.setFontSize(7);
        doc.setTextColor(128, 128, 128);
        doc.text('Generated by Flokraft Ultimate DanceSport Management System © 2025',
                doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 5, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      }

      // Generate filename
      const sanitizedCompetitionName = competitionName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `${sanitizedCompetitionName}_dancers_checklist_${new Date().toISOString().split('T')[0]}.pdf`;

      // Save the PDF
      doc.save(filename);

      console.log('✅ Dancers List PDF generated successfully:', filename);

    } catch (error) {
      console.error('❌ Error generating Dancers List PDF:', error);
      alert(`Failed to generate Dancers List PDF: ${error.message}`);
    }
  };

  return (
    <div className="couples-list-container">
      <div className="couples-list-header">
        <div className="header-left">
          <h2>{readOnly ? 'Checked-In Dancers' : 'Dancer(s) Management'}</h2>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by number, name, club, location, or event..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        {!readOnly && (
          <div className="button-group">
            <button className="add-couple-btn" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : 'Add New Dancer(s)'}
            </button>
            <button
              className="pdf-export-btn"
              onClick={generateDancersListPDF}
              title="Generate Dancers List PDF"
            >
              <span className="pdf-icon">📄</span>
              Create PDF
            </button>
          </div>
        )}
      </div>

      {/* Display summary statistics - part of fixed header */}
      {!loading && (() => {
        const displayCouples = getFilteredCouples();
        const hasSearchQuery = searchQuery.trim() !== '';

        if (displayCouples.length > 0) {
          const regCheckedIn = displayCouples.filter(c => c.registrationCheckin).length;

          return (
            <div className="couples-summary">
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-number">{displayCouples.length}</span>
                  <span className="stat-label">{hasSearchQuery ? 'Found Entries' : 'Total Entries'}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {displayCouples.filter(c => (c.dancerType || 'couple') === 'solo').length}
                  </span>
                  <span className="stat-label">Solo Dancers</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {displayCouples.filter(c => (c.dancerType || 'couple') === 'couple').length}
                  </span>
                  <span className="stat-label">Couples</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {displayCouples.filter(c => (c.dancerType || 'couple') === 'team').length}
                  </span>
                  <span className="stat-label">Teams</span>
                </div>
                {!readOnly && (
                  <div className="stat-item checkin-stat">
                    <span className="stat-number">{regCheckedIn}</span>
                    <span className="stat-label">Registration Checked In</span>
                  </div>
                )}
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Fixed Table Headers - part of fixed header */}
      <div className="table-headers-container">
        <table className="couples-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Type</th>
              <th>Name(s)</th>
              <th>Country</th>
              <th>Club/Studio</th>
              <th>Event</th>
              <th>Payment Status</th>
              {!readOnly && <th>Registration</th>}
              {!readOnly && <th>Actions</th>}
            </tr>
          </thead>
        </table>
      </div>

      {/* Couple Form - Show when button in header is clicked */}
      {!readOnly && showForm && (
        <div className="couple-form-fixed">
          <CoupleForm
            existingCouple={editingCouple}
            onCoupleSubmitted={handleCoupleSubmitted}
            onCancel={() => {
              setShowForm(false);
              setEditingCouple(null);
            }}
          />
        </div>
      )}

      {/* EXTRA SAFEGUARD: Force hide any form elements in read-only mode */}
      {readOnly && showForm && (
        <div style={{display: 'none'}}></div>
      )}

      <div className="scrollable-content">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {loading ? (
        <div className="loading-message">Loading dancers...</div>
      ) : (
        (() => {
          const filteredCouples = getFilteredCouples();
          const hasSearchQuery = searchQuery.trim() !== '';

          if (filteredCouples.length === 0) {
            return (
              <div className="empty-message">
                {hasSearchQuery
                  ? `No dancers found matching "${searchQuery}". Try a different search term.`
                  : readOnly
                    ? 'No dancers available. Only dancers checked in after event allocation are shown here.'
                    : 'No dancers found. Add your first dancer(s) to get started.'
                }
              </div>
            );
          }

          return (
            <div>
              {hasSearchQuery && (
                <div className="search-results-info">
                  Found {filteredCouples.length} dancer{filteredCouples.length !== 1 ? 's' : ''} matching "{searchQuery}"
                </div>
              )}
              <div className="couples-table-container">
                <table className="couples-table">
                  <thead>
                    <tr>
                      <th>Number</th>
                      <th>Type</th>
                      <th>Name(s)</th>
                      <th>Country</th>
                      <th>Club/Studio</th>
                      <th>Event</th>
                      <th>Payment Status</th>
                      {!readOnly && <th>Registration</th>}
                      {!readOnly && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCouples
                      .sort((a, b) => {
                        // Sort by cloth number numerically
                        const numA = parseInt(a.clothNumber) || 0;
                        const numB = parseInt(b.clothNumber) || 0;
                        return numA - numB;
                      })
                      .map((couple) => {
                  const dancerNames = formatDancerNames(couple);
                  const dancerType = getDancerTypeLabel(couple);
                  const isAllocated = isDancerAllocatedToEvent(couple, events);

                  return (
                    <tr key={couple.id} className={isAllocated ? 'allocated-dancer' : 'unallocated-dancer'}>
                      <td>
                        <div
                          className={`cloth-number-badge ${isAllocated ? 'clickable' : 'disabled'}`}
                          onClick={isAllocated ? () => handleClothNumberClick(couple) : undefined}
                          title={isAllocated ? "Click to generate cloth number display" : "Cloth number available only for allocated dancers"}
                        >
                          {couple.clothNumber}
                        </div>
                      </td>
                      <td>
                        <span className={`dancer-type-badge ${couple.dancerType || 'couple'}`}>
                          {dancerType}
                        </span>
                      </td>
                      <td className="dancer-names">
                        {dancerNames}
                      </td>
                      <td>
                        <span className="country-name">
                          {getDancerLocation(couple)}
                        </span>
                      </td>
                      <td>
                        <span className="club-name">
                          {getClubNameById(couple.clubId)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`event-name-badge ${expandedEvents.has(couple.id) ? 'expanded' : ''}`}
                          onClick={() => toggleEventExpansion(couple.id)}
                          title={`Click to ${expandedEvents.has(couple.id) ? 'collapse' : 'expand'} events`}
                        >
                          {expandedEvents.has(couple.id)
                            ? getAllCoupleEvents(couple)
                            : getAllCoupleEvents(couple).substring(0, 40) + (getAllCoupleEvents(couple).length > 40 ? '...' : '')
                          }
                        </span>
                      </td>
                      <td className="payment-status-cell">
                        <span className={`payment-status-badge ${couple.paymentStatus ? 'paid' : 'unpaid'}`}>
                          <span className="status-indicator"></span>
                          {couple.paymentStatus ? 'Paid' : 'Not Paid'}
                        </span>
                      </td>
                      {!readOnly && (
                        <td className="checkin-cell">
                          <label className="checkin-toggle">
                            <input
                              type="checkbox"
                              checked={couple.registrationCheckin || false}
                              onChange={() => handleCheckinUpdate(couple.id, couple.registrationCheckin || false)}
                              disabled={updatingCheckin[`${couple.id}-registrationCheckin`]}
                            />
                            <span className="checkin-slider"></span>
                            <span className="checkin-label">
                              {updatingCheckin[`${couple.id}-registrationCheckin`] ? '...' : (couple.registrationCheckin ? 'Checked In' : 'Not Checked')}
                            </span>
                          </label>
                        </td>
                      )}
                      {!readOnly && (
                        <td className="couple-actions">
                          <button
                            className="edit-couple-btn"
                            onClick={() => handleEditCouple(couple)}
                            title={`Edit ${dancerNames}`}
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-couple-btn"
                            onClick={() => handleDeleteCouple(couple.id, dancerNames)}
                            title={`Delete ${dancerNames}`}
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                            </tbody>
                </table>
              </div>
            </div>
          );
        })()
      )}
      </div>

      {/* Cloth Number Popup */}
      <ClothNumberPopup
        isOpen={showClothPopup}
        onClose={() => setShowClothPopup(false)}
        clothNumber={selectedClothData?.clothNumber}
        clubStudio={selectedClothData?.clubStudio}
        coupleNames={selectedClothData?.coupleNames}
        eventName={selectedClothData?.eventTitle}
      />
    </div>
  );
};

export default CouplesList;